import { SignIn } from '@clerk/clerk-react'
import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import OnboardingAuthShell from '@/components/onboarding/OnboardingAuthShell'
import {
  buildClaimRedirect,
  getClaimParams,
  getMarketingUrlParam,
} from '@/utils/onboardingScan'
import {
  buildSubscriptionCheckoutPath,
  buildSubscriptionSignUpPath,
  getSubscriptionCheckoutIntent,
} from '@/utils/subscriptionCheckoutIntent'
import { safeInternalPath } from '@/utils/safeInternalPath'

export default function SignInPage() {
  const [searchParams] = useSearchParams()
  const { domain } = getClaimParams(searchParams)
  const prefillUrl = getMarketingUrlParam(searchParams)

  const redirectUrl = useMemo(() => {
    const claimRedirect = buildClaimRedirect(searchParams, '')
    const checkoutIntent = getSubscriptionCheckoutIntent(searchParams)
    if (checkoutIntent) return buildSubscriptionCheckoutPath(checkoutIntent)
    /**
     * `returnTo` carries the page the visitor was trying to reach, which is how
     * the welcome results page sends someone here to sign in and then get their
     * own report back. Without it every such visitor lands on /dashboard, which
     * looks like the report was lost. Validated, because an unchecked redirect
     * target taken from a query string is an open redirect.
     */
    const returnTo = safeInternalPath(searchParams.get('returnTo'))
    return claimRedirect || returnTo || '/dashboard'
  }, [searchParams])

  const signUpUrl = useMemo(() => {
    const claimRedirect = buildClaimRedirect(searchParams, '')
    const checkoutIntent = getSubscriptionCheckoutIntent(searchParams)
    if (checkoutIntent) return buildSubscriptionSignUpPath(checkoutIntent)
    if (claimRedirect || prefillUrl || searchParams.get('returnTo')) return `/sign-up?${searchParams.toString()}`
    return '/sign-up'
  }, [prefillUrl, searchParams])

  useEffect(() => {
    document.title = domain
      ? `Claim ${domain} Site Scan Report | SuperSchema`
      : 'Sign In | SuperSchema'
  }, [domain])

  return (
    <OnboardingAuthShell mode="sign-in" domain={domain} prefillUrl={prefillUrl} switchHref={signUpUrl}>
      <SignIn
        appearance={{
          elements: {
            /*
              Structure only. Everything about how this looks now lives in
              `clerk.css`, in tokens, so both themes come out of the same
              rules instead of a wall of light-theme literals here and sixty
              lines correcting them for dark in index.css.

              These four are layout plumbing that neutralises Clerk's own
              card so it can sit inside ours, plus the offset that centres a
              provider icon beside its label.
            */
            rootBox: 'w-full max-w-[520px] mx-auto flex justify-center overflow-visible',
            cardBox: 'w-full max-w-[520px] mx-auto !shadow-none shadow-none !border-0 border-0 ring-0 bg-transparent overflow-visible',
            card: 'w-full max-w-[520px] mx-auto !shadow-none shadow-none !border-0 border-0 ring-0 p-0 bg-transparent overflow-visible',
            headerTitle: 'hidden',
            headerSubtitle: 'hidden',
            socialButtonsBlockButton: 'relative h-12 w-full justify-center overflow-visible px-6',
            socialButtonsBlockButtonArrow: 'hidden',
            socialButtonsProviderIcon: 'absolute left-1/2 -translate-x-[5.8rem]',
          }
        }}
        forceRedirectUrl={redirectUrl}
        redirectUrl={redirectUrl}
        signUpUrl={signUpUrl}
      />
    </OnboardingAuthShell>
  )
}
